import React, { useEffect, useState } from 'react';
import AppShell from '@/components/AppShell';
import CompanyGate from '@/components/CompanyGate';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatINR } from '@/lib/format';
import { extractError } from '@/lib/errors';
import { DatePicker } from '@/components/ui/date-picker';
import DeleteConfirmDialog from '@/components/DeleteConfirmDialog';

export default function Vendors() {
  const { effectiveCompanyId } = useAuth();
  return (
    <AppShell title="Vendors" subtitle="Master list of vendors and their purchase summaries.">
      <CompanyGate>
        <VendorsContent companyId={effectiveCompanyId} />
      </CompanyGate>
    </AppShell>
  );
}

function VendorsContent({ companyId }) {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', contact_person: '', phone: '', email: '', address: '', gstin: '', notes: '', default_credit_days: '' });
  const [summary, setSummary] = useState({});
  const [delTarget, setDelTarget] = useState(null);
  // Wave 3.36 — Pending Bills cross-vendor report dialog state.
  const [pendingDlgOpen, setPendingDlgOpen] = useState(false);
  const [pendingCutoff, setPendingCutoff] = useState('');
  const [pendingBusy, setPendingBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get('/masters/vendors', { params: { company_id: companyId } });
    setList(data);
    // Load summary per vendor for the last 90 days
    const sums = {};
    await Promise.all(data.slice(0, 100).map(async (v) => {
      try {
        const { data: s } = await api.get(`/masters/vendors/${v.id}/summary`);
        sums[v.id] = s.totals;
      } catch {}
    }));
    setSummary(sums);
  };
  useEffect(() => { if (companyId) load(); }, [companyId]);

  const openDialog = (v) => {
    if (v) {
      setEditing(v); setForm({
        name: v.name,
        contact_person: v.contact_person || '',
        phone: v.phone || '',
        email: v.email || '',
        address: v.address || '',
        gstin: v.gstin || '',
        notes: v.notes || '',
        default_credit_days: v.default_credit_days != null ? String(v.default_credit_days) : '',
      });
    } else {
      setEditing(null); setForm({ name: '', contact_person: '', phone: '', email: '', address: '', gstin: '', notes: '', default_credit_days: '' });
    }
    setOpen(true);
  };

  const submit = async () => {
    try {
      // Wave 3.36 — Normalize default_credit_days. Empty string → null.
      const payload = { ...form };
      payload.default_credit_days =
        form.default_credit_days === '' || form.default_credit_days == null
          ? null
          : Math.max(0, parseInt(form.default_credit_days, 10) || 0);
      if (editing) {
        await api.put(`/masters/vendors/${editing.id}`, payload);
        toast.success('Vendor updated');
      } else {
        await api.post('/masters/vendors', { ...payload, company_id: companyId });
        toast.success('Vendor created');
      }
      setOpen(false); load();
    } catch (e) { toast.error(extractError(e, 'Failed')); }
  };

  // Wave 3.36 — Download the cross-vendor pending-bills PDF for a chosen cutoff.
  const downloadPendingBills = async () => {
    if (!pendingCutoff) {
      toast.error('Please pick a cutoff date.');
      return;
    }
    setPendingBusy(true);
    try {
      const params = { company_id: companyId, due_on_or_before: pendingCutoff };
      const res = await api.get('/masters/vendors/pending-bills/pdf', {
        params, responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pending_bills_vendors_${pendingCutoff}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Pending bills report downloaded');
      setPendingDlgOpen(false);
    } catch (e) {
      toast.error(extractError(e, 'Failed to generate report'));
    } finally {
      setPendingBusy(false);
    }
  };

  return (
    <div>
      <div className="sticky-filter-bar flex items-center justify-between mb-3 rounded-lg px-3" data-testid="vendors-filter-bar">
        <div>
          <div className="text-base font-medium text-[var(--ink-navy)]">Vendor Master</div>
          <div className="text-xs text-slate-500">From whom you purchase diamonds and other materials.</div>
        </div>
        <div className="flex items-center gap-2">
          {/* Wave 3.36 — Cross-vendor Pending Bills report */}
          <Button
            variant="outline"
            onClick={() => { setPendingCutoff(''); setPendingDlgOpen(true); }}
            data-testid="vendors-pending-bills-button"
          >
            Pending Bills
          </Button>
          <Button className="luxury-button" onClick={() => openDialog(null)} data-testid="vendor-add-button"><Plus size={14} className="mr-1.5" /> New Vendor</Button>
        </div>
      </div>
      <Card className="overflow-hidden">
        <div className="ledger-table overflow-x-auto">
          <table className="w-full">
            <thead><tr>
              <th className="text-left">Vendor</th>
              <th className="text-left">Contact</th>
              <th className="text-left">Phone / Email</th>
              <th className="text-left">GSTIN</th>
              <th className="text-right">Purchases (all-time)</th>
              <th className="text-right">Value</th>
              <th className="text-right">Actions</th>
            </tr></thead>
            <tbody data-testid="vendors-table-body">
              {list.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-500">No vendors yet</td></tr>}
              {list.map((v) => (
                <tr key={v.id} data-testid={`vendor-row-${v.id}`}>
                  <td>
                    <Link to={`/vendors/${v.id}`} className="font-medium text-slate-800 hover:text-[var(--ink-navy)] hover:underline" data-testid={`vendor-open-${v.id}`}>{v.name}</Link>
                  </td>
                  <td className="text-sm text-slate-600">{v.contact_person}</td>
                  <td className="text-xs text-slate-500">{v.phone}<br/>{v.email}</td>
                  <td className="text-xs font-mono">{v.gstin}</td>
                  <td className="text-right text-xs font-mono tabular-nums">
                    {/* Hotfix — display txns + non-zero metrics across all 4 materials.
                        Some vendors are gold-only (grams), others raw-stone (carats). */}
                    {(() => {
                      const s = summary[v.id] || {};
                      const ct = Number(s.carats || 0);
                      const gm = Number(s.grams || 0);
                      const parts = [];
                      if (ct > 0) parts.push(`${ct.toFixed(2)} ct`);
                      if (gm > 0) parts.push(`${gm.toFixed(2)} g`);
                      const tail = parts.length ? ` · ${parts.join(' · ')}` : '';
                      return `${s.transactions || 0} txns${tail}`;
                    })()}
                  </td>
                  <td className="text-right font-mono tabular-nums">{formatINR(summary[v.id]?.value || 0)}</td>
                  <td className="text-right">
                    <Link to={`/vendors/${v.id}`} className="text-xs text-[var(--ink-navy)] hover:underline mr-2 inline-flex items-center" data-testid={`vendor-view-${v.id}`}><ExternalLink size={12} className="mr-1" />View</Link>
                    <button onClick={() => openDialog(v)} className="text-xs text-[var(--ink-navy)] hover:underline mr-2" data-testid={`vendor-edit-${v.id}`}><Pencil size={12} className="inline mr-1" />Edit</button>
                    <button onClick={() => setDelTarget(v)} className="text-xs text-red-600 hover:underline" data-testid={`vendor-delete-${v.id}`}><Trash2 size={12} className="inline mr-1" />Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Vendor' : 'New Vendor'}</DialogTitle><DialogDescription>Capture supplier information for purchase entries.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vendor-name-input" /></div>
            <div><Label>Contact Person</Label><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>GSTIN</Label><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} /></div>
            <div>
              <Label>Default Credit Days</Label>
              <Input
                type="number"
                min={0}
                max={365}
                placeholder="e.g. 30 (leave blank for none)"
                value={form.default_credit_days}
                onChange={(e) => setForm({ ...form, default_credit_days: e.target.value.replace(/[^0-9]/g, '') })}
                data-testid="vendor-default-credit-days-input"
              />
              <div className="text-[10px] text-slate-500 mt-0.5">Pre-fills the credit period on new purchase entries.</div>
            </div>
            <div className="col-span-2"><Label>Address</Label><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="luxury-button" data-testid="vendor-submit-button">{editing ? 'Save' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wave 3.36 — Cross-vendor Pending Bills report dialog */}
      <Dialog open={pendingDlgOpen} onOpenChange={setPendingDlgOpen}>
        <DialogContent className="max-w-md" data-testid="vendors-pending-bills-dialog">
          <DialogHeader>
            <DialogTitle>Pending Bills Report</DialogTitle>
            <DialogDescription>
              Print every outstanding vendor bill whose due date falls on or before the chosen cutoff. Bills are grouped by vendor and sorted by due date.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <Label className="text-xs text-slate-500">Due on or before *</Label>
              <DatePicker
                value={pendingCutoff}
                onChange={(e) => setPendingCutoff(e.target.value)}
                data-testid="vendors-pending-bills-cutoff"
              />
              <div className="text-[10px] text-slate-500 mt-0.5">
                Bills with no due date are excluded.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDlgOpen(false)}>Cancel</Button>
            <Button
              onClick={downloadPendingBills}
              disabled={pendingBusy || !pendingCutoff}
              className="luxury-button"
              data-testid="vendors-pending-bills-download"
            >
              {pendingBusy ? 'Generating…' : 'Download PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {delTarget && (
        <DeleteConfirmDialog
          open={!!delTarget}
          onOpenChange={(v) => { if (!v) setDelTarget(null); }}
          entity_type="vendor"
          entity_id={delTarget.id}
          entity_label={delTarget.name}
          company_id={companyId}
          onSuccess={() => { setDelTarget(null); load(); }}
        />
      )}
    </div>
  );
}
